import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalVariant } from '@/features/configuration-management/location/table/Column';
import { useDynamicLocale } from '@/hoslog-intl';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { CustomerReferralStageList } from '@/services/pointConfiguration/pointConfiguration.types';
import { REFERRAL_STATUS } from '@/services/referralStage/referralStage.types';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { Button } from 'ui/button';
import { ManageReferralStageContainer } from '../ManageReferralStageContainer';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  t: ReturnType<typeof useDynamicLocale>['t'],
  onOpen: OnOpenProps,
  updateStatus: (payload: { stageId: string; status: 'A' | 'B' }) => Promise<unknown>,
  openConfirmation: (data: {
    title: string;
    description?: string | undefined;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
): ColumnDef<CustomerReferralStageList>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="max-w-[10rem] truncate text-[12px] font-[500]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'stageTitle',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.referralStage.table.stageTitle')}
        />
      ),
      cell: ({ row }) => (
        <div className="max-w-[20rem] truncate text-[12px] font-[500]">{row.getValue('stageTitle')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.referralStage.table.createdBy')}
        />
      ),
      cell: ({ row }) => (
        <div className="max-w-[20rem] truncate text-[12px] font-[500]">{row.getValue('createdBy')}</div>
      ),
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.referralStage.table.createdDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="max-w-[20rem] truncate text-[12px] font-[500]">
          {formatDate(Number(row.getValue('createdDate')))}
        </div>
      ),
      meta: {
        label: t('common.page.tableColumn.createdDate'),
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.referralStage.table.status')}
        />
      ),
      cell: ({ row }) =>
        row.original.status === REFERRAL_STATUS.ACTIVE ? (
          <div className="text-success text-[12px] font-[500]">{t('common.page.table.active')}</div>
        ) : (
          <div className="text-destructive text-[12px] font-[500]">{t('common.page.table.blocked')}</div>
        ),
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const isActive = row.original.status === REFERRAL_STATUS.ACTIVE;
        return (
          <div className="flex gap-4 items-center">
            {row.original.status === REFERRAL_STATUS.ACTIVE && (
              <Can
                rule={{
                  allOf: [PERMS.point.management.referralStage.manage, PERMS.point.management.referralStage.detail],
                }}
              >
                <EditIcon
                  className="text-foreground-subtle size-4 hover:cursor-pointer"
                  onClick={() =>
                    onOpen({
                      title: 'pointManagement.pointsConfiguration.manageReferralStage.title',
                      okText: t('common.page.modal.submit'),
                      cancelText: t('common.page.modal.cancel'),
                      size: 'lg',
                      modalTitle: 'pointManagement.pointsConfiguration.manageReferralStage.title',
                      component: <ManageReferralStageContainer stageId={row.original.stageId} index={row.index} />,
                    })
                  }
                />
              </Can>
            )}
            <Can rule={{ allOf: [PERMS.point.management.referralStage.statusUpdate] }}>
              <Button
                variant="ghost"
                className="p-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: isActive ? t('common.page.modal.blockMessage') : t('common.page.modal.unblockMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    variant: 'warning',
                    onAction: async () => {
                      await updateStatus({
                        stageId: row.original.stageId,
                        status: isActive ? 'B' : 'A',
                      });
                    },
                    onCancel: () => {},
                  });
                }}
              >
                {isActive ? (
                  <LockCloseIcon className="text-destructive size-4" />
                ) : (
                  <LockOpenIcon className="text-info size-4" />
                )}
              </Button>
            </Can>
          </div>
        );
      },
      size: 40,
    },
  ];
}
